import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";

const PROMPT_LABEL = {
  challenged: "Challenged",
  agreed: "Agreed with",
  disagreed: "Disagreed with",
  wrestling: "Wrestling with",
  connection: "Connection",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function Journal() {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    api.get("/journal").then(({ data }) => setEntries(data)).catch(() => setEntries([]));
  }, []);

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Intellectual Journal</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3" data-testid="journal-title">
          A record of your thinking.
        </h1>
        <p className="text-sm text-forge-100/60 mb-12 max-w-2xl">
          Each entry is anchored to a reading. Revisit your past selves — that is where growth becomes visible.
        </p>

        {entries === null ? (
          <p className="text-sm text-forge-100/40">Loading…</p>
        ) : entries.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-sm p-10 text-center">
            <p className="text-sm text-forge-100/60 mb-4">
              Journal entries appear after you complete a reading and choose Write Journal Entry.
            </p>
            <Link to="/learn" className="inline-block px-5 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold rounded-sm tracking-wide transition-all">
              Start the learning loop
            </Link>
          </div>
        ) : (
          <ul className="space-y-10" data-testid="journal-list">
            {entries.map((j) => (
              <li key={j.session_id} className="border border-white/10 rounded-sm p-6 sm:p-8 hover:border-ember-500/40 transition-colors">
                <div className="flex justify-between items-baseline mb-4 flex-wrap gap-2">
                  <h2 className="font-serif text-2xl text-forge-100">{j.title || "Untitled reading"}</h2>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-forge-100/40">{formatDate(j.updated_at)}</span>
                </div>
                <div className="space-y-5">
                  {Object.entries(j.entries || {}).map(([k, v]) => (
                    v ? (
                      <div key={k}>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gold-500 mb-1">{PROMPT_LABEL[k] || k}</div>
                        <p className="font-reading text-base sm:text-lg leading-relaxed text-forge-100/85">{v}</p>
                      </div>
                    ) : null
                  ))}
                </div>
                <Link to={`/reading/${j.session_id}`} className="inline-block mt-6 text-xs uppercase tracking-[0.25em] text-forge-100/50 hover:text-ember-400 transition-colors">
                  Open reading →
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
